/**
 * Spending the context budget: plan, gist, splice, and only then drop.
 *
 * `pure/contextBudget.ts` decides WHAT to compress and can be simulated
 * headlessly. This is the half that needs the model, because the gist is a
 * generation. Every call in this protocol is a one-shot, so the extra latency is
 * one short completion at the moment the window would otherwise have overflowed.
 *
 * THE ORDER IS THE POLICY, and it is Free's:
 *
 *   1. Fold the oldest think blocks into a rolling first-person gist. They have
 *      already done their work; each shaped a retrieval and an answer that both
 *      survive, so a note is the cheapest thing to lose.
 *   2. Rewrite the standing gist in the same call, so the past-thoughts block
 *      stays one or two sentences no matter how many exchanges it has absorbed.
 *   3. Only when there is nothing left to fold, drop the oldest exchange whole.
 *
 * Step 3 DROPS rather than summarises, and the distinction is the rule the whole
 * module exists to keep. A transcript that quietly paraphrases what someone said
 * is worse than one that runs out of room, because a rebuttal would then answer
 * words nobody used. What falls out of the window is gone; what remains is
 * verbatim.
 *
 * Nothing here is silent. Every fold and every drop is reported, because a
 * transcript that lost a turn without saying so is indistinguishable from one
 * where the model simply forgot.
 */

import type { MindClient } from "./llm/client";
import type { ChatMessage } from "./llm/protocol";
import {
  planCompression,
  applyCompression,
  totalTokens,
  dropOldestExchange,
  dropFacts,
  gistPrompt,
  clampGist,
  type BudgetedMsg,
} from "./pure/contextBudget.ts";
import { cleanDeliberation } from "./pure/deliberate.ts";

export type FitReport = {
  before: number;
  after: number;
  /** Think blocks folded into the gist this call. */
  folded: number;
  /** Turns dropped outright, once folding ran out. Should normally be zero. */
  dropped: number;
  /** The memory banner was given up. The last lever, and the loudest signal. */
  factsDropped: boolean;
  /** The rolling past-thoughts block, if one was written. */
  gist?: string;
  /** True when even dropping could not get under the limit. */
  stillOver: boolean;
  ms: number;
};

export const NO_CHANGE: FitReport = {
  before: 0,
  after: 0,
  folded: 0,
  dropped: 0,
  factsDropped: false,
  stillOver: false,
  ms: 0,
};

/**
 * Bring a labelled context under `limit`, writing the gist with one model call.
 *
 * `preamble` is the speaker's persona. The gist is spliced back in as that
 * agent's own turn, so it has to sound like the agent; a summary in a neutral
 * register would read as somebody describing it, which is the same category
 * error that made a prefilled note get answered rather than continued.
 */
export async function fitToBudget(
  llm: MindClient,
  msgs: BudgetedMsg[],
  limit: number,
  opts: { preamble?: string; maxGistChars?: number } = {},
): Promise<{ msgs: BudgetedMsg[]; report: FitReport }> {
  const t0 = performance.now();
  const before = totalTokens(msgs);
  if (before <= limit) {
    return { msgs, report: { ...NO_CHANGE, before, after: before } };
  }

  let out = msgs;
  let gist: string | undefined;
  let folded = 0;

  const plan = planCompression(msgs, limit);
  if (plan.compress.length > 0) {
    // The standing gist is rewritten rather than appended to, so it goes in as
    // context and comes back absorbed. Notes are the new material.
    const standing = plan.compress
      .filter((i) => msgs[i].kind === "gist")
      .map((i) => msgs[i].content)
      .join(" ");
    const notes = plan.compress.filter((i) => msgs[i].kind === "note").map((i) => msgs[i].content);
    folded = notes.length;

    if (notes.length > 0) {
      const messages: ChatMessage[] = [
        ...(opts.preamble ? [{ role: "system" as const, content: opts.preamble }] : []),
        { role: "user", content: gistPrompt(notes, standing || undefined) },
      ];
      // The prefix is about to be rewritten wholesale, so the cached one is
      // worthless before the call and misleading after it.
      llm.resetCache();
      const res = await llm.generate(messages, { maxNewTokens: 120, enableThinking: false });
      llm.resetCache();
      gist = clampGist(cleanDeliberation(res.text), opts.maxGistChars);
    }

    // A model that returned nothing must not be allowed to erase the notes it
    // failed to summarise. Better to stay over budget and let the drop path
    // handle it visibly.
    if (gist) out = applyCompression(msgs, plan, gist);
    else folded = 0;
  }

  // Then the oldest speech, a whole exchange at a time. Loops because one may
  // not be enough, and terminates because `dropOldestExchange` returns its input
  // unchanged when it is spent.
  let dropped = 0;
  while (totalTokens(out) > limit) {
    const next = dropOldestExchange(out);
    if (next === out) break;
    dropped += out.length - next.length;
    out = next;
  }

  // And only then the memory banner, which is grounding and is given up last.
  let factsDropped = false;
  if (totalTokens(out) > limit) {
    const next = dropFacts(out);
    if (next !== out) {
      factsDropped = true;
      out = next;
    }
  }

  const after = totalTokens(out);
  return {
    msgs: out,
    report: {
      before, after, folded, dropped, factsDropped, gist,
      stillOver: after > limit,
      ms: performance.now() - t0,
    },
  };
}

/** One line, for a log or a dev harness. Never silent about what it spent. */
export function describeFit(r: FitReport): string {
  if (!r.folded && !r.dropped && !r.factsDropped) return `${r.before} tokens, under budget`;
  const parts = [`${r.before} -> ${r.after} tokens`];
  if (r.folded) parts.push(`${r.folded} note${r.folded === 1 ? "" : "s"} folded`);
  if (r.dropped) parts.push(`${r.dropped} turn${r.dropped === 1 ? "" : "s"} DROPPED`);
  if (r.factsDropped) parts.push(`MEMORY BANNER DROPPED`);
  if (r.stillOver) parts.push(`STILL OVER`);
  return `${parts.join(", ")} in ${Math.round(r.ms)}ms`;
}
